import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { getRequest } from "services/api";
import { buildQueryString } from "utils/queryHelpers";

const AttendanceSettingContext = createContext();

export const AttendanceSettingProvider = ({ children }) => {
  const [attendanceSettings, setAttendanceSettings] = useState({
    data: [],
    pagination: null,
    error: null,
    loading: false,
  });

  const [filterOptions, setFilterOptions] = useState({
    search: "",
    currentPage: 1,
    totalPages: 1,
    include: "office,user",
  });

  const queryString = buildQueryString({
    search: filterOptions.search || "",
    page: filterOptions.currentPage,
    include: filterOptions.include || "office,user",
  });

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setFilterOptions((prevState) => ({
        ...prevState,
        search: e.target.value,
      }));
    }
  };

  const fetchAttendanceSettings = useCallback(async () => {
    setAttendanceSettings((prevState) => ({
      ...prevState,
      loading: true,
    }));

    try {
      const response = await getRequest(`/ahr/absensi-settings${queryString}`);
      if (response.success) {
        setAttendanceSettings({
          data: response.data,
          pagination: response.pagination,
          error: null,
          loading: false,
        });

        setFilterOptions((prev) => ({
          ...prev,
          totalPages: response.pagination.last_page,
        }));
      }
    } catch (error) {
      setAttendanceSettings({
        data: [],
        pagination: null,
        error: error.message,
        loading: false,
      });
    }
  }, [queryString]);

  useEffect(() => {
    fetchAttendanceSettings();
  }, [fetchAttendanceSettings]);

  const handlePageChange = (page) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      currentPage: page,
    }));
  };

  const totalPages = attendanceSettings.pagination?.last_page || 1;

  const PaginationComponent = usePaginationWithComponent({
    totalPages: totalPages,
    currentPage: filterOptions.currentPage,
    onPageChange: handlePageChange,
    visiblePages: 5,
  });

  const Pagination = () => PaginationComponent;

  return (
    <AttendanceSettingContext.Provider
      value={{
        attendanceSettings,
        filterOptions,
        handleSearch,
        refetch: fetchAttendanceSettings,
        Pagination,
      }}
    >
      {children}
    </AttendanceSettingContext.Provider>
  );
};

export const useAttendanceSetting = () => {
  const context = useContext(AttendanceSettingContext);
  if (!context) {
    throw new Error(
      "useAttendanceSetting must be used within AttendanceSettingProvider"
    );
  }

  const fetchOfficeOptions = async (inputValue = "") => {
    const response = await getRequest(
      `/ahr/offices?search=${inputValue}&limit=50`
    );
    if (response.success) {
      return response.data.map((item) => ({
        value: item.id,
        label: item.name,
      }));
    }
    return [];
  };

  const fetchUserOptions = async (inputValue = "") => {
    const response = await getRequest(`/user?search=${inputValue}&all=1&include=roles`);
    if (response.success) {
      // Filter users with role "user_hr"
      const hrUsers = response.data.filter(user =>
        user.roles && user.roles.some(role => role.name === "user_hr")
      );
      return hrUsers.map((item) => ({
        value: item.id,
        label: item.name,
      }));
    }
    return [];
  };

  const loadOfficeOptions = useCallback(async (inputValue) => {
    return await fetchOfficeOptions(inputValue);
  }, []);

  const loadUserOptions = useCallback(async (inputValue) => {
    return await fetchUserOptions(inputValue);
  }, []);

  return {
    ...context,
    fetchOfficeOptions,
    fetchUserOptions,
    loadOfficeOptions,
    loadUserOptions,
  };
};
